export enum MessageDirection {
  INBOUND = 'INBOUND',
  OUTBOUND = 'OUTBOUND'
}

export interface Message {
  id: number;
  body: string;
  createdAt: Date | string;
  type: MessageDirection;
}

const MESSAGE_SEPARATOR = '---MESSAGE_SEPARATOR---';

const HTML_CLEANING_PATTERNS = {
  FOOTER: /<div[^>]*style="[^"]*border-top:\s*1px\s+solid\s+#999[^"]*"[^>]*>[\s\S]*?<\/div>/gi,
  BUTTONS: /<(?:a|button|div)[^>]*(?:class="[^"]*(?:btn|button|MAGI)[^"]*"|style="[^"]*(?:background|padding|border)[^"]*")[^>]*>[\s\S]*?<\/(?:a|button|div)>/gi,
  CENTER_ELEMENTS: /<(?:center|div)[^>]*(?:align="center"|style="[^"]*text-align:\s*center[^"]*")[^>]*>[\s\S]*?<\/(?:center|div)>/gi
} as const;

const HTML_ENTITIES = {
  '&nbsp;': ' ',
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'"
} as const;

const sortByDate = (messages: Message[]): Message[] => 
  [...messages].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

const compareMessages = (a: Message, b: Message): number => {
  const dateA = new Date(a.createdAt).getTime();
  const dateB = new Date(b.createdAt).getTime();
  return isNaN(dateA) || isNaN(dateB) || dateA === dateB ? a.id - b.id : dateA - dateB;
};

export const getMessagesForProfile = (messages: any[]): Message[] => {
  if (!messages?.length) return [];

  const typedMessages = messages as Message[];
  const sortedMessages = sortByDate(typedMessages);
  const outboundMessages = sortedMessages.filter(msg => 
    msg.type === MessageDirection.OUTBOUND || (msg.type as any) === 'OUTBOUND'
  );
  const inboundMessages = sortedMessages.filter(msg => 
    msg.type === MessageDirection.INBOUND || (msg.type as any) === 'INBOUND'
  );
  
  const latestInbound = inboundMessages[inboundMessages.length - 1] || null;
  const messagesToDisplay: Message[] = [];

  if (outboundMessages.length > 0) {
    const latest = outboundMessages[outboundMessages.length - 1];
    const finalOutbound = outboundMessages.length > 1 
      ? {
          ...latest,
          body: outboundMessages
            .map(msg => msg.body)
            .filter(body => body.trim())
            .join(`\n${MESSAGE_SEPARATOR}\n`)
        }
      : latest;

    if (latestInbound) {
      const shouldOutboundFirst = compareMessages(finalOutbound, latestInbound) <= 0;
      messagesToDisplay.push(
        ...(shouldOutboundFirst ? [finalOutbound, latestInbound] : [latestInbound, finalOutbound])
      );
    } else {
      messagesToDisplay.push(finalOutbound);
    }
  } else if (latestInbound) {
    messagesToDisplay.push(latestInbound);
  }

  return messagesToDisplay.sort(compareMessages);
};

const removeHtmlElements = (content: string): string => {
  let cleanContent = content;
  Object.values(HTML_CLEANING_PATTERNS).forEach(pattern => {
    cleanContent = cleanContent.replace(pattern, '');
  });
  return cleanContent;
};

const removeHtmlTags = (content: string): string => {
  return content
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]*>/g, '');
};

const decodeHtmlEntities = (content: string): string => {
  let decodedContent = content;
  Object.entries(HTML_ENTITIES).forEach(([entity, replacement]) => {
    decodedContent = decodedContent.replace(new RegExp(entity, 'g'), replacement);
  });
  return decodedContent;
};

const normalizeLines = (content: string): string[] => {
  return content
    .split('\n')
    .map(line => line.trim())
    .filter(line => line.length > 0);
};

const deduplicateLines = (lines: string[]): string[] => {
  return lines.filter((line, i) => i === 0 || line !== lines[i - 1]);
};

const addFormatting = (lines: string[]): string[] => {
  const formattedLines: string[] = [];
  lines.forEach((line, i) => {
    formattedLines.push(line);
    if (
      line && 
      (line.includes('Thank you') || line.includes('City of') || 
       line.match(/^\d+.*(?:Street|Ave|Avenue|Road|Rd|Dr|Drive|Blvd|Boulevard)/i)) &&
      i < lines.length - 1 && lines[i + 1]
    ) {
      formattedLines.push('');
    }
  });
  return formattedLines;
};

export const cleanMessageForProfile = (messageBody: string): string => {
  if (!messageBody) return '';

  let content = messageBody;
  content = removeHtmlElements(content);
  content = removeHtmlTags(content);
  content = decodeHtmlEntities(content);

  let lines = normalizeLines(content);
  lines = deduplicateLines(lines);
  lines = addFormatting(lines);

  return lines.join('\n').trim();
};

export const getLatestMessage = (messages: Message[]): Message | null => {
  if (!messages?.length) return null;
  const sorted = sortByDate(messages);
  return sorted[sorted.length - 1];
};

export const isLatestMessage = (message: Message, messages: Message[]): boolean => {
  const latest = getLatestMessage(messages);
  return latest?.id === message.id;
};

export const formatMessageTimestamp = (dateTime: Date | string): string => {
  if (typeof dateTime === 'string' && dateTime.includes('/') && dateTime.includes(':')) {
    const parts = dateTime.split(' ');
    if (parts.length === 2) {
      return `${parts[0]} at ${parts[1]}`;
    }
  }
  
  const date = new Date(dateTime);
  if (isNaN(date.getTime())) {
    return String(dateTime);
  }
  
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'pm' : 'am';
  
  hours = hours % 12;
  hours = hours ? hours : 12;
  
  return `${month}/${day}/${year} at ${hours}:${minutes}${ampm}`;
};

export const getAllMessagesForChat = (messages: unknown[]): Message[] => {
  if (!Array.isArray(messages) || messages.length === 0) {
    return [];
  }

  try {
    const typedMessages = messages as Message[];
    return typedMessages.map(message => ({
      ...message,
      body: cleanMessageForProfile(message.body)
    }));
  } catch (error) {
    console.warn('Error processing messages for chat:', error);
    return [];
  }
}; 